import styled from "@emotion/styled";
import { type WidgetSettingsDefinition } from "@repo/api";
import {
  Button,
  ListViewItem,
  Slider,
  SliderInputControl,
  Text,
  moneyCashIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { ModalSheet, SectionContainer, translateKey } from "@repo/ui";
import { useState } from "react";

const SliderDescription = styled(Text)`
  ::first-letter {
    text-transform: uppercase;
  }
`;

const getValueLabel = (value: number[]) => {
  const getLabel = (val: number) =>
    val <= 0
      ? t("label.expired_days_ago", { days: val * -1 })
      : t("label.expiring_in_days", { days: val });

  return `${getLabel(value[0])} - ${getLabel(value[1])}`;
};

export const QuotationRangeSettingsControl = ({
  settingDefinition,
  setting,
  setSetting,
}: {
  settingDefinition: WidgetSettingsDefinition;
  setting: Record<string, any>;
  setSetting: (newSetting: Record<string, object>) => void;
}) => {
  const defaultValue = [
    Number(settingDefinition.defaultValue?.[0]),
    Number(settingDefinition.defaultValue?.[1]),
  ];

  const value: number[] = setting[settingDefinition.id] || defaultValue;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newValue, setNewValue] = useState(value);

  const valueLabel = getValueLabel(newValue);

  return (
    <>
      <ListViewItem
        control="navigational"
        title={translateKey(settingDefinition.title)}
        description={value ? valueLabel : undefined}
        onClick={() => setModalIsOpen(true)}
        leadingIcon={moneyCashIcon}
      />
      <ModalSheet
        title={translateKey(settingDefinition.title)}
        visible={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        onBack={() => setModalIsOpen(false)}
        footer={
          <>
            <Button
              type="primary"
              text={t("action.confirm")}
              onClick={() => {
                setSetting({
                  ...setting,
                  [settingDefinition.id]: newValue,
                });
                setModalIsOpen(false);
              }}
            />
            <Button
              type="secondary"
              text={t("action.delete")}
              onClick={() => {
                setSetting({
                  ...setting,
                  [settingDefinition.id]: undefined,
                });
                setModalIsOpen(false);
                setNewValue(defaultValue);
              }}
            />
          </>
        }
      >
        <SectionContainer>
          <Slider
            label={t("label.quotation_widget_slider")}
            id="value"
            ariaLabels={["From", "To"]}
            range={{
              max: 365,
              min: -365,
            }}
            start={newValue}
            onChange={setNewValue}
            ariaFormat={{
              to: (val) => val.toString(),
            }}
            suffixLabel={t("label.days")}
            inputControls={<SliderInputControl type="text" />}
          />

          <SliderDescription tagName="p">{valueLabel}</SliderDescription>
        </SectionContainer>
      </ModalSheet>
    </>
  );
};
